import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  EmojiEvents as EmojiEventsIcon,
  FilterList as FilterListIcon,
  Groups as GroupsIcon,
  HeadsetMic as HeadsetMicIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  WorkspacePremium as WorkspacePremiumIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import SysDateRangePicker from '../../components/SysDateRangePicker';
import SetoresAllSelect from './componentes/SetoresAllSelect';
import ContatoAtendimentoDetalhesDialog from './componentes/ContatoAtendimentoDetalhesDialog';
import AtendimentosRankingListaDialog, { AtendimentosRankingListaFilters } from '../../components/modals/AtendimentosRankingListaDialog';
import { atendimentoService, AtendimentoContato, AtendimentoRankingContato } from '../../services/atendimentoService';

const CORES_AVATAR = ['#1565c0', '#6a1b9a', '#2e7d32', '#c62828', '#e65100', '#0277bd', '#4a148c', '#1b5e20', '#880e4f', '#33691e'];

const MEDALHAS = [
  { cor: '#FFD700', bg: 'linear-gradient(135deg, #fff8e1 0%, #ffe082 100%)', icone: '🥇' },
  { cor: '#C0C0C0', bg: 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)', icone: '🥈' },
  { cor: '#CD7F32', bg: 'linear-gradient(135deg, #fff3e0 0%, #ffcc80 100%)', icone: '🥉' },
];

const dateToYmd = (date: Date | null) => {
  if (!date) return undefined;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getInitials = (name: string) =>
  name ? name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase() : '?';

const formatarTelefone = (numero?: string | null, ddi?: string | null): string => {
  if (!numero) return '-';
  let limpo = numero.replace(/\D/g, '');

  let ddiEncontrado = ddi || '';
  if (!ddiEncontrado && limpo.startsWith('55') && (limpo.length === 12 || limpo.length === 13)) {
    ddiEncontrado = '55';
  }

  if (ddiEncontrado && limpo.startsWith(ddiEncontrado)) {
    limpo = limpo.slice(ddiEncontrado.length);
  }

  if (limpo.length === 10) {
    return `${ddiEncontrado ? `+${ddiEncontrado} ` : ''}(${limpo.slice(0, 2)}) ${limpo.slice(2, 6)}-${limpo.slice(6)}`;
  }
  if (limpo.length === 11) {
    return `${ddiEncontrado ? `+${ddiEncontrado} ` : ''}(${limpo.slice(0, 2)}) ${limpo.slice(2, 7)}-${limpo.slice(7)}`;
  }

  return numero;
};

interface KpiCardProps {
  titulo: string;
  valor: string | number;
  subtitulo?: string;
  icone: React.ReactNode;
  cor: string;
  bgCor: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ titulo, valor, subtitulo, icone, cor, bgCor }) => (
  <Card
    elevation={0}
    sx={{
      height: '100%',
      borderRadius: 2,
      border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.8)}`,
    }}
  >
    <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
        <Box minWidth={0}>
          <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
            {titulo}
          </Typography>
          <Typography variant="h6" fontWeight={800} color={cor} lineHeight={1.2}>
            {valor}
          </Typography>
          {subtitulo && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              {subtitulo}
            </Typography>
          )}
        </Box>
        <Avatar sx={{ bgcolor: bgCor, width: 34, height: 34, '& .MuiSvgIcon-root': { fontSize: 18 } }}>{icone}</Avatar>
      </Box>
    </CardContent>
  </Card>
);

interface PodiumCardProps {
  posicao: number;
  contato: AtendimentoRankingContato;
  onContatoClick: (contato: AtendimentoRankingContato) => void;
  onClienteClick: (contato: AtendimentoRankingContato) => void;
  onAtendimentosClick: (contato: AtendimentoRankingContato) => void;
}

const clickableLinkSx = {
  cursor: 'pointer',
  '&:hover': { color: 'primary.main', textDecoration: 'underline' },
};

const clickableChipSx = {
  cursor: 'pointer',
  '&:hover': { opacity: 0.85, transform: 'scale(1.03)' },
};

const rankingContatoToAtendimentoContato = (contato: AtendimentoRankingContato): AtendimentoContato => ({
  id: contato.id,
  nome: contato.nome_contato,
  ddi: contato.ddi || '55',
  numero: contato.numero || '',
  remote_jid: '',
  canal: 'whatsapp',
  is_grupo: false,
  bot_desativado: false,
  cliente_id: contato.cliente_id ?? null,
  cliente: contato.cliente_id && contato.nome_cliente
    ? { id: contato.cliente_id, nome: contato.nome_cliente }
    : undefined,
});

const PodiumCard: React.FC<PodiumCardProps> = ({ posicao, contato, onContatoClick, onClienteClick, onAtendimentosClick }) => {
  const theme = useTheme();
  const medalha = MEDALHAS[posicao - 1];
  const corAvatar = CORES_AVATAR[contato.id % CORES_AVATAR.length];
  const altura = posicao === 1 ? 108 : posicao === 2 ? 96 : 88;

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pt: posicao === 1 ? 0 : 1.5,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          minHeight: altura,
          p: 1.25,
          borderRadius: 2,
          textAlign: 'center',
          background: medalha.bg,
          border: `1.5px solid ${alpha(medalha.cor, 0.45)}`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            bgcolor: medalha.cor,
          },
        }}
      >
        <Typography variant="body1" sx={{ mb: 0.25, lineHeight: 1 }}>
          {medalha.icone}
        </Typography>
        <Avatar
          sx={{
            width: 38,
            height: 38,
            mx: 'auto',
            mb: 0.5,
            bgcolor: corAvatar,
            fontWeight: 700,
            fontSize: '0.8rem',
            border: `2px solid ${medalha.cor}`,
          }}
        >
          {getInitials(contato.nome_contato)}
        </Avatar>
        <Typography
          variant="caption"
          fontWeight={700}
          noWrap
          display="block"
          title={contato.nome_contato}
          onClick={() => onContatoClick(contato)}
          sx={clickableLinkSx}
        >
          {contato.nome_contato}
        </Typography>
        {contato.nome_cliente && (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            display="block"
            title={contato.nome_cliente}
            sx={{ fontSize: '0.65rem', ...clickableLinkSx }}
            onClick={() => onClienteClick(contato)}
          >
            {contato.nome_cliente}
          </Typography>
        )}
        <Chip
          label={`${contato.atendimentos} atend.`}
          size="small"
          onClick={() => onAtendimentosClick(contato)}
          sx={{
            mt: 0.5,
            height: 20,
            fontSize: '0.65rem',
            fontWeight: 700,
            bgcolor: alpha(medalha.cor, 0.2),
            color: theme.palette.mode === 'dark' ? theme.palette.common.white : '#5d4037',
            ...clickableChipSx,
          }}
        />
      </Paper>
      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mt: 0.5, fontSize: '0.65rem' }}>
        {posicao}º lugar
      </Typography>
    </Box>
  );
};

const RankingPosicao: React.FC<{ posicao: number }> = ({ posicao }) => {
  if (posicao <= 3) {
    const medalha = MEDALHAS[posicao - 1];
    return (
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 800,
          fontSize: '0.75rem',
          bgcolor: alpha(medalha.cor, 0.2),
          color: medalha.cor,
          border: `1.5px solid ${alpha(medalha.cor, 0.5)}`,
        }}
      >
        {posicao}
      </Box>
    );
  }

  return (
    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ width: 26, textAlign: 'center' }}>
      {posicao}
    </Typography>
  );
};

const RankingPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [contatos, setContatos] = useState<AtendimentoRankingContato[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [somenteClientes, setSomenteClientes] = useState(false);
  const [setorId, setSetorId] = useState<number | null>(null);
  const [dateRange, setDateRange] = useState<{ startDate: Date | null; endDate: Date | null }>({
    startDate: null,
    endDate: null,
  });
  const [contatoDetalhes, setContatoDetalhes] = useState<AtendimentoContato | null>(null);
  const [listaModal, setListaModal] = useState<{ title: string; subtitle?: string; filters: AtendimentosRankingListaFilters } | null>(null);

  const fetchRanking = useCallback(async () => {
    setLoading(true);
    try {
      const dataInicio = dateToYmd(dateRange.startDate);
      const dataFim = dateToYmd(dateRange.endDate);
      const params: {
        data_criacao_inicio?: string;
        data_criacao_fim?: string;
        setor_id?: number;
      } = {};

      if (dataInicio && dataFim) {
        params.data_criacao_inicio = dataInicio;
        params.data_criacao_fim = dataFim;
      }
      if (setorId) {
        params.setor_id = setorId;
      }

      const data = await atendimentoService.getRanking(Object.keys(params).length ? params : undefined);
      setContatos(Array.isArray(data) ? data : []);
    } catch {
      enqueueSnackbar('Erro ao carregar ranking', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [dateRange.startDate, dateRange.endDate, setorId, enqueueSnackbar]);

  useEffect(() => {
    fetchRanking();
  }, [fetchRanking]);

  useEffect(() => {
    setPage(0);
  }, [somenteClientes, dateRange.startDate, dateRange.endDate, setorId]);

  const contatosFiltrados = useMemo(() => {
    if (!somenteClientes) return contatos;
    return contatos.filter((contato) => Boolean(contato.nome_cliente));
  }, [contatos, somenteClientes]);

  const contatosPaginados = useMemo(
    () => contatosFiltrados.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [contatosFiltrados, page, rowsPerPage]
  );

  const resumo = useMemo(() => {
    const totalAtendimentos = contatosFiltrados.reduce((acc, c) => acc + c.atendimentos, 0);
    const comCliente = contatosFiltrados.filter((c) => Boolean(c.nome_cliente)).length;
    const top = contatosFiltrados.find((c) => c.atendimentos > 0);
    const media = contatosFiltrados.length
      ? (totalAtendimentos / contatosFiltrados.length).toFixed(1)
      : '0';

    return { totalAtendimentos, comCliente, top, media };
  }, [contatosFiltrados]);

  const topTres = useMemo(
    () => contatosFiltrados.filter((c) => c.atendimentos > 0).slice(0, 3),
    [contatosFiltrados]
  );

  const maxAtendimentos = useMemo(
    () => Math.max(...contatosFiltrados.map((c) => c.atendimentos), 1),
    [contatosFiltrados]
  );

  const buildListaFilters = (contatoId: number): AtendimentosRankingListaFilters => {
    const filters: AtendimentosRankingListaFilters = { contato_id: contatoId };
    const dataInicio = dateToYmd(dateRange.startDate);
    const dataFim = dateToYmd(dateRange.endDate);
    if (dataInicio && dataFim) {
      filters.data_criacao_inicio = dataInicio;
      filters.data_criacao_fim = dataFim;
    }
    if (setorId) {
      filters.setor_id = setorId;
    }
    return filters;
  };

  const abrirDetalhesContato = (contato: AtendimentoRankingContato) => {
    setContatoDetalhes(rankingContatoToAtendimentoContato(contato));
  };

  const abrirPaginaCliente = (contato: AtendimentoRankingContato) => {
    if (contato.cliente_id) {
      navigate(`/clients/${contato.cliente_id}`);
    }
  };

  const abrirListaAtendimentos = (contato: AtendimentoRankingContato) => {
    setListaModal({
      title: `Atendimentos — ${contato.nome_contato}`,
      subtitle: `${contato.atendimentos} atendimento${contato.atendimentos !== 1 ? 's' : ''} no período`,
      filters: buildListaFilters(contato.id),
    });
  };

  const headerGradient =
    theme.palette.mode === 'dark'
      ? `linear-gradient(135deg, ${alpha(theme.palette.warning.dark, 0.35)} 0%, ${alpha(theme.palette.primary.main, 0.25)} 50%, ${alpha('#1a1a2e', 0.9)} 100%)`
      : `linear-gradient(135deg, ${alpha('#fff8e1', 0.95)} 0%, ${alpha(theme.palette.primary.light, 0.15)} 45%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`;

  return (
    <Box sx={{ p: { xs: 1, md: 1.5 },  mx: 'auto' }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          mb: 1.5,
          p: { xs: 1.25, md: 1.5 },
          borderRadius: 2,
          background: headerGradient,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -20,
            right: -10,
            opacity: 0.08,
            transform: 'rotate(15deg)',
          }}
        >
          <EmojiEventsIcon sx={{ fontSize: 120, color: theme.palette.warning.main }} />
        </Box>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
              boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.35)}`,
            }}
          >
            <EmojiEventsIcon sx={{ fontSize: 22 }} />
          </Avatar>
          <Box flex={1} minWidth={0}>
            <Typography variant="h6" fontWeight={800} lineHeight={1.2}>
              Ranking de Atendimentos
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Contatos ordenados pela quantidade de atendimentos
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* KPIs */}
      {!loading && contatosFiltrados.length > 0 && (
        <Grid container spacing={1} mb={1.5}>
          <Grid item xs={6} md={3}>
            <KpiCard
              titulo="Total de Contatos"
              valor={contatosFiltrados.length}
              icone={<GroupsIcon sx={{ color: theme.palette.primary.main }} />}
              cor={theme.palette.primary.main}
              bgCor={alpha(theme.palette.primary.main, 0.12)}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <KpiCard
              titulo="Total Atendimentos"
              valor={resumo.totalAtendimentos}
              icone={<HeadsetMicIcon sx={{ color: theme.palette.success.main }} />}
              cor={theme.palette.success.main}
              bgCor={alpha(theme.palette.success.main, 0.12)}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <KpiCard
              titulo="Com Cliente"
              valor={resumo.comCliente}
              subtitulo={`${contatosFiltrados.length ? Math.round((resumo.comCliente / contatosFiltrados.length) * 100) : 0}% do total`}
              icone={<PersonIcon sx={{ color: theme.palette.info.main }} />}
              cor={theme.palette.info.main}
              bgCor={alpha(theme.palette.info.main, 0.12)}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <KpiCard
              titulo="Média por Contato"
              valor={resumo.media}
              subtitulo={resumo.top ? `Top: ${resumo.top.atendimentos}` : undefined}
              icone={<TrendingUpIcon sx={{ color: theme.palette.warning.main }} />}
              cor={theme.palette.warning.main}
              bgCor={alpha(theme.palette.warning.main, 0.12)}
            />
          </Grid>
        </Grid>
      )}

      {/* Pódio Top 3 */}
      {!loading && topTres.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            mb: 1.5,
            p: { xs: 1.25, md: 1.5 },
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.background.paper, 0.6),
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.75} mb={1}>
            <WorkspacePremiumIcon sx={{ color: theme.palette.warning.main, fontSize: 18 }} />
            <Typography variant="body2" fontWeight={700}>
              Top 3 Contatos
            </Typography>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={5} alignItems="stretch">
            {topTres.length >= 2 && (
              <PodiumCard
                posicao={2}
                contato={topTres[1]}
                onContatoClick={abrirDetalhesContato}
                onClienteClick={abrirPaginaCliente}
                onAtendimentosClick={abrirListaAtendimentos}
              />
            )}
            {topTres.length >= 1 && (
              <PodiumCard
                posicao={1}
                contato={topTres[0]}
                onContatoClick={abrirDetalhesContato}
                onClienteClick={abrirPaginaCliente}
                onAtendimentosClick={abrirListaAtendimentos}
              />
            )}
            {topTres.length >= 3 && (
              <PodiumCard
                posicao={3}
                contato={topTres[2]}
                onContatoClick={abrirDetalhesContato}
                onClienteClick={abrirPaginaCliente}
                onAtendimentosClick={abrirListaAtendimentos}
              />
            )}
          </Stack>
        </Paper>
      )}

      {/* Filtros */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.25, md: 1.5 },
          mb: 1.5,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.04 : 0.02),
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75} mb={1}>
          <FilterListIcon color="primary" sx={{ fontSize: 18 }} />
          <Typography variant="body2" fontWeight={700}>
            Filtros
          </Typography>
        </Stack>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} md={4}>
            <SysDateRangePicker
              label="Data Atendimento"
              value={dateRange}
              onChange={(range) => setDateRange(range)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <SetoresAllSelect
              value={setorId}
              onChange={setSetorId}
              label="Setor"
              placeholder="Todos os setores"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                px: 1.25,
                py: 0.25,
                borderRadius: 1.5,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: theme.palette.background.paper,
                display: 'inline-flex',
              }}
            >
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={somenteClientes}
                    onChange={(e) => setSomenteClientes(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <StarIcon sx={{ fontSize: 15, color: somenteClientes ? 'warning.main' : 'text.disabled' }} />
                    <Typography variant="caption" fontWeight={600}>
                      Somente Clientes
                    </Typography>
                  </Stack>
                }
              />
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabela */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 0.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" fontWeight={700}>
            Listagem Completa
          </Typography>
          {!loading && (
            <Chip
              size="small"
              label={`${contatosFiltrados.length} contato${contatosFiltrados.length !== 1 ? 's' : ''}`}
              color="primary"
              variant="outlined"
              sx={{ height: 22, fontSize: '0.7rem' }}
            />
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 5, gap: 1 }}>
            <CircularProgress size={32} />
            <Typography variant="caption" color="text.secondary">
              Carregando ranking...
            </Typography>
          </Box>
        ) : contatosFiltrados.length === 0 ? (
          <Box sx={{ p: 2.5, textAlign: 'center' }}>
            <EmojiEventsIcon sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.25), mb: 1 }} />
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Nenhum contato encontrado
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Ajuste os filtros ou aguarde novos atendimentos.
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                      '& .MuiTableCell-head': {
                        fontWeight: 700,
                        fontSize: '0.68rem',
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        color: 'text.secondary',
                        py: 0.75,
                        px: 1,
                        borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                      },
                    }}
                  >
                    <TableCell width={44}>#</TableCell>
                    <TableCell width={56}>Id</TableCell>
                    <TableCell>Contato</TableCell>
                    <TableCell>Nome Cliente</TableCell>
                    <TableCell>Telefone</TableCell>
                    <TableCell align="right">Atendimentos</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contatosPaginados.map((contato, index) => {
                    const posicaoGlobal = page * rowsPerPage + index + 1;
                    const corAvatar = CORES_AVATAR[contato.id % CORES_AVATAR.length];
                    const intensidade = contato.atendimentos / maxAtendimentos;

                    return (
                      <TableRow
                        key={contato.id}
                        hover
                        sx={{
                          '& .MuiTableCell-root': { py: 0.6, px: 1 },
                          ...(posicaoGlobal <= 3 && contato.atendimentos > 0
                            ? { bgcolor: alpha(MEDALHAS[posicaoGlobal - 1].cor, 0.04) }
                            : {}),
                        }}
                      >
                        <TableCell>
                          <RankingPosicao posicao={posicaoGlobal} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            {contato.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            <Avatar
                              sx={{
                                width: 28,
                                height: 28,
                                bgcolor: corAvatar,
                                fontSize: '0.65rem',
                                fontWeight: 700,
                              }}
                            >
                              {getInitials(contato.nome_contato)}
                            </Avatar>
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              noWrap
                              onClick={() => abrirDetalhesContato(contato)}
                              sx={clickableLinkSx}
                            >
                              {contato.nome_contato}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {contato.nome_cliente ? (
                            <Chip
                              label={contato.nome_cliente}
                              size="small"
                              variant="outlined"
                              color="info"
                              clickable
                              onClick={() => abrirPaginaCliente(contato)}
                              sx={{ maxWidth: 180, height: 22, fontSize: '0.68rem', fontWeight: 500 }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.disabled">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <PhoneIcon sx={{ fontSize: 13, color: 'success.main', opacity: 0.8 }} />
                            <Typography variant="caption" color="text.secondary">
                              {formatarTelefone(contato.numero, contato.ddi)}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={contato.atendimentos}
                            size="small"
                            onClick={() => abrirListaAtendimentos(contato)}
                            sx={{
                              height: 22,
                              fontSize: '0.72rem',
                              fontWeight: 800,
                              minWidth: 36,
                              bgcolor: alpha(theme.palette.primary.main, 0.08 + intensidade * 0.25),
                              color: intensidade > 0.6 ? theme.palette.primary.dark : theme.palette.primary.main,
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.2 + intensidade * 0.3)}`,
                              ...clickableChipSx,
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Divider />
            <TablePagination
              component="div"
              size="small"
              count={contatosFiltrados.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Linhas:"
              sx={{
                minHeight: 44,
                '.MuiTablePagination-toolbar': { minHeight: 44, px: 1.5 },
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  fontSize: '0.75rem',
                  my: 0,
                },
              }}
            />
          </>
        )}
      </Paper>

      <ContatoAtendimentoDetalhesDialog
        open={Boolean(contatoDetalhes)}
        contato={contatoDetalhes}
        onClose={() => setContatoDetalhes(null)}
      />

      <AtendimentosRankingListaDialog
        open={Boolean(listaModal)}
        onClose={() => setListaModal(null)}
        title={listaModal?.title || ''}
        subtitle={listaModal?.subtitle}
        filters={listaModal?.filters || { contato_id: 0 }}
      />
    </Box>
  );
};

export default RankingPage;
